import { Role } from "../../../core/models/entities/Role";
import {
  MyAdminProfile,
  MyCustomerProfile,
  MyProfileViewModel,
  MyVendorProfile,
} from "./my-profile.component";
import { useMyAdminProfile } from "./useProfile/use-my-admin-profile";
import { useMyVendorProfile } from "./useProfile/use-my-vendor-profile";
import { useMyCustomerProfile } from "./useProfile/use-my-customer-profile";
import { ErrorView } from "../../components/error-view/ErrorView";
import { LoadingView } from "../../components/loading-view/LoadingView";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../shared/hooks/useComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Organization } from "../../../core/models/entities/Organization";

/**
 * The role is static for the whole session, so we branch to one role-specific
 * component and let THAT component call its hooks unconditionally. This
 * replaces the old pattern of picking a viewmodel hook inside a switch, which
 * violated the rules of hooks.
 */
export function MyProfileScreen(): React.ReactElement {
  const { role } = useProfileContext();

  switch (role!) {
    case Role.Admin:
      return <AdminProfileScreen />;
    case Role.Vendor:
      return <VendorProfileScreen />;
    case Role.EndUser:
      return <CustomerProfileScreen />;
  }
}

function AdminProfileScreen(): React.ReactElement {
  const { profile } = useProfileContext();
  const vm = useMyAdminProfile({ profile: profile! });
  return useRoleProfileView(vm, MyAdminProfile);
}

function VendorProfileScreen(): React.ReactElement {
  const { profile } = useProfileContext();
  const { organizationRepository } = useRepositoriesContext();
  const { data: vendor } = organizationRepository.useVendor(
    profile!.userOrganizationInformations.organizationId!
  );

  const vm = useMyVendorProfile({ profile: profile!, organization: vendor! });
  return useRoleProfileView(vm, MyVendorProfile, vendor);
}

function CustomerProfileScreen(): React.ReactElement {
  const { profile } = useProfileContext();
  const { organizationRepository } = useRepositoriesContext();
  const { data: customer } = organizationRepository.useCustomer(
    profile!.userOrganizationInformations.organizationId!
  );

  const vm = useMyCustomerProfile({
    profile: profile!,
    organization: customer!,
  });
  return useRoleProfileView(vm, MyCustomerProfile, customer);
}

/**
 * Custom hook shared by the three role screens: drives the loading state
 * machine and renders the role component once the viewmodel is ready. Each
 * role screen calls it unconditionally, so the hook order is stable.
 */
function useRoleProfileView(
  vm: MyProfileViewModel,
  Component: (props: MyProfileViewModel) => React.ReactElement,
  organization?: Organization
): React.ReactElement {
  const isLoading = vm?.loading === true;
  const isSuccess = vm?.loading === false;
  const isError = vm?.loading === undefined;

  const { state, dataTransition } = useComponentStateMachine();
  dataTransition(isLoading, isSuccess, isError);

  return state === ComponentState.Succeed ? (
    <Component {...vm} organization={organization ?? vm.organization} />
  ) : state === ComponentState.Loading ? (
    <LoadingView />
  ) : (
    <ErrorView />
  );
}
